import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import {
  Modal, ModalBody, ModalHeader, Progress,
} from 'reactstrap';
import styled from 'styled-components';

const LoadProgress = styled(Progress).attrs({
  className: 'my-2 rounded-pill',
})`
  height: 1.5rem;
`;

const LoadProgressBar = styled(Progress).attrs({
  className: 'rounded-pill',
})``;

/**
 * @typedef {object} LoadingOverlayInputs
 * @property {string} headerLabel - The text to display in the modal header.
 * @property {string} bodyLabel - The text to display in the modal body.
 * @property {string} [progressLabel] - The text to display below the progress bar.
 * @property {number} [progressBarValue=50] - The number of periods(chunks) already loaded.
 * @property {Function} updateProgress - A callback function to be called when the modal opens.
 */

/**
 * LoadingOverlay component displays a modal with a loading progress bar.
 * It is designed to block user interaction during a loading process.
 * @param {object} props - The component's props.
 * @param {LoadingOverlayInputs} props.inputs - An object containing all the inputs
 *  to configure the loading overlay.
 * @returns {React.ReactElement|null} The LoadingOverlay modal component or null.
 */
function LoadingOverlay({ inputs }) {
  if (!inputs || Object.keys(inputs).length === 0) {
    return null;
  }
  // If the loaded value(periodsLoaded) is not present, the progress will be shown at the 50% range.
  const {
    headerLabel, bodyLabel, progressLabel,
    updateProgress, progressBarValue = 50,
  } = inputs;
  const modalRef = useRef(null);

  /**
   * Focuses the modal when it opens to ensure accessibility.
   */
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  return (
    <Modal isOpen innerRef={modalRef} backdrop="static" keyboard={false} centered onOpened={() => updateProgress(true)}>
      <ModalHeader>
        <h5 className="modal-title">{headerLabel}</h5>
      </ModalHeader>
      <ModalBody>
        <p>{bodyLabel}</p>
        <LoadProgress multi>
          <LoadProgressBar
            animated
            bar
            value={progressBarValue}
            color="primary"
          />
        </LoadProgress>
        {progressLabel && <p>{progressLabel}</p>}
      </ModalBody>
    </Modal>
  );
}

export default LoadingOverlay;

LoadingOverlay.propTypes = {
  inputs: PropTypes.shape({
    headerLabel: PropTypes.string.isRequired,
    bodyLabel: PropTypes.string.isRequired,
    progressBarValue: PropTypes.number,
    progressLabel: PropTypes.string,
    updateProgress: PropTypes.func.isRequired,
  }),
};

LoadingOverlay.defaultProps = {
  inputs: {
    progressBarValue: 50,
    progressLabel: null,
  },
};
